import { useMutation } from "@apollo/client";
import { parseError } from "apollo";
import Button from "components/base/Button";
import { Input, Text } from "components/base/Form";
import { Alert } from "components/base/Toast";
import ContainerizationView from "components/shared/ContainerizationView";
import { useModals } from "ModalProvider";
import { useContext, useReducer, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { UPDATE_SHIPMENT } from "./graphql";
import {
  UPDATE_SHIPMENT_INVOICE_ROW,
  UPDATE_SHIPMENT_INVOICE_ROW_PRODUCT,
} from "./invoices/graphql";
import Attachments from "./Attachments";
import BookingFormButton from "../documents/BookingFormButton";
import BomExportForm from "../documents/BomExportForm";
import CustomerAddressSelector from "./customer/CustomerAddressSelector";
import CustomerInfo from "./customer/CustomerInfo";
import PackingListButton from "../documents/packingList/Button";
import GenerateShippingMark from "./shippingMark/Generate";
import ShipmentInvoices from "./invoices";
import Logistics from "./logistics";
import NotifyCustomerTemplateList from "./notifications/TemplateList";
import NotifyCustomerView from "./notifications/View";
import ProductSelector from "./ProductSelector";
import reducer from "./reducer";
import ShipmentContext from "./ShipmentContext";
import ShipmentStatus from "./status";
import StatusForm from "./status/StatusForm";

const REQUIRED_SHIPMENT_FIELD_LABELS = {
  deliveryName: "Delivery name",
  deliveryAddr: "Delivery address",
  notifyPartyName: "Notify party name",
  notifyParty: "Notify party address",
  consigneeName: "Consignee name",
  addr: "Consignee address",
};

function ShipmentContainer({ shipment, shipmentMeta }) {
  const [state, dispatch] = useReducer(reducer, {
    ...shipment,
  });

  return (
    <ShipmentContext.Provider
      value={{
        state,
        dispatch,
      }}
    >
      <ShipmentForm shipmentMeta={shipmentMeta} />
    </ShipmentContext.Provider>
  );
}

function ShipmentForm({ shipmentMeta }) {
  const { state, dispatch } = useContext(ShipmentContext);

  const [updateShipmentMutation, updateShipmentRes] = useMutation(
    UPDATE_SHIPMENT,
    {
      onError: (err) => Alert("error", err.message),
    },
  );
  const [updateShipmentInvoiceRowProduct] = useMutation(
    UPDATE_SHIPMENT_INVOICE_ROW_PRODUCT,
  );
  const [updateShipmentInvoiceRowMutation] = useMutation(
    UPDATE_SHIPMENT_INVOICE_ROW,
    {
      onCompleted: ({ updateShipmentInvoiceRow }) => {
        dispatch({
          type: "updateShipmentInvoiceRow",
          payload: updateShipmentInvoiceRow,
        });
        Alert("success", "Row updated.");
      },
      onError: (error) => Alert("error", parseError(error)),
    },
  );

  const navigate = useNavigate();
  const shippingMarkModal = useModals();
  const chooseCustomerAddressModal = useModals();
  const changeProductModal = useModals();
  const notifyCustomerModal = useModals();
  const showTemplatesModal = useModals();
  const bomSelectorModal = useModals();
  const shipmentSaveQueue = useRef(Promise.resolve());
  const rowSaveQueue = useRef(Promise.resolve());

  function onChangeShipment(payload) {
    dispatch({ type: "changeShipment", payload });
  }

  function enqueueShipmentUpdate(variables) {
    // Keep blur saves ordered so an earlier request cannot overwrite a later edit.
    const update = shipmentSaveQueue.current
      .catch(() => undefined)
      .then(() => updateShipmentMutation({ variables }));
    shipmentSaveQueue.current = update;
    return update;
  }

  function validateRequiredShipmentFields(fields) {
    const missing = fields
      .filter(
        ([key, value]) =>
          REQUIRED_SHIPMENT_FIELD_LABELS[key] && !String(value || "").trim(),
      )
      .map(([key]) => REQUIRED_SHIPMENT_FIELD_LABELS[key]);
    if (!missing.length) return true;
    Alert("error", `Please fill in ${missing.join(", ")}.`);
    return false;
  }

  function updateShipment(fields) {
    // Remove only leading/trailing whitespace; line breaks inside addresses and marks are meaningful.
    const trimmedFields = Object.fromEntries(
      Object.entries(fields).map(([key, value]) => [
        key,
        typeof value === "string" ? value.trim() : value,
      ]),
    );
    if (!validateRequiredShipmentFields(Object.entries(trimmedFields))) return;
    onChangeShipment(trimmedFields);
    return enqueueShipmentUpdate({
      shipmentInput: { id: state.id, ...trimmedFields },
    })
      .then((res) => {
        if (res) Alert("success", "Shipment updated.");
        return res;
      })
      .catch(() => undefined);
  }

  function updateShipmentInvoiceRow(variables) {
    const update = rowSaveQueue.current
      .catch(() => undefined)
      .then(() => updateShipmentInvoiceRowMutation({ variables }));
    rowSaveQueue.current = update;
    return update.catch(() => undefined);
  }

  function prepareShippingMark(customer, shipmentInvoiceRow) {
    shippingMarkModal.present({
      title: "Shipping Mark",
      maxWidth: "w-full",
      children: (
        <GenerateShippingMark customer={customer} row={shipmentInvoiceRow} />
      ),
    });
  }

  function changeShipmentInvoiceRowProduct(shipmentInvoiceRowId) {
    changeProductModal.present({
      title: "Change Product",
      children: (
        <ProductSelector
          onSelectProduct={(product) => {
            changeProductModal.hide();
            updateShipmentInvoiceRowProduct({
              variables: { shipmentInvoiceRowId, productId: product.id },
            })
              .then(({ data }) => {
                dispatch({
                  type: "updateShipmentInvoiceRow",
                  payload: data.updateShipmentInvoiceRowProduct,
                });
                Alert("success", "Product changed.");
              })
              .catch((error) => {
                Alert("error", parseError(error));
              });
          }}
        />
      ),
    });
  }

  function chooseCustomerAddress(forType) {
    chooseCustomerAddressModal.present({
      title: "Choose Address",
      subtitle: state.customer.name,
      children: (
        <CustomerAddressSelector
          customer={state.customer}
          onSelect={(address) => {
            didChooseAddress(forType, address);
          }}
        />
      ),
    });
  }

  function didChooseAddress(forType, address) {
    chooseCustomerAddressModal.hide();
    let fields;
    if (forType === "addr") {
      fields = {
        [forType]: address.address,
        consigneeName: address.name,
      };
    } else if (forType === "deliveryAddr") {
      fields = {
        [forType]: address.address,
        deliveryName: address.name,
      };
    } else if (forType === "notifyParty") {
      fields = {
        [forType]: address.address,
        notifyPartyName: address.name,
      };
    }
    updateShipment(fields);
  }

  function showCustomFormGenerator() {
    if (
      !validateRequiredShipmentFields(
        ["consigneeName", "addr"].map((key) => [key, state[key]]),
      )
    ) {
      return;
    }
    navigate(`/shipment/doc/custom-form/${state.id}`);
  }

  function canGeneratePackingList() {
    return validateRequiredShipmentFields(
      ["consigneeName", "addr", "deliveryName", "deliveryAddr"].map((key) => [
        key,
        state[key],
      ]),
    );
  }

  const invoiceNumber = state.shipmentInvoices
    .map((i) => i.invoice.number)
    .join(" / ");

  const templateNotesRef = useRef("");

  function showNotifyCustomerModal() {
    const emails = [
      state.customer.user.email,
      ...state.customer.subAccounts.map((i) => i.user.email),
      ...state.extraEmails.split(",").filter((i) => !!i),
    ];
    notifyCustomerModal.present({
      title: "Notify Customer",
      subtitle: state.customer.name,
      children: (
        <NotifyCustomerView
          shipmentId={state.id}
          initialNote={templateNotesRef.current}
          customerName={state.customer.name}
          invoiceNumber={invoiceNumber}
          emails={emails}
          hide={notifyCustomerModal.hide}
        />
      ),
    });
  }

  function showTemplates() {
    if (
      !validateRequiredShipmentFields([["deliveryAddr", state.deliveryAddr]])
    ) {
      return;
    }
    showTemplatesModal.present({
      title: "Select a template",
      children: (
        <NotifyCustomerTemplateList
          shipmentId={state.id}
          onSelect={(template) => {
            const notes = template
              .replace("{customername}", state.customer.name)
              .replace("{invoicenumber}", invoiceNumber);
            templateNotesRef.current = notes;
            showTemplatesModal.hide();
            showNotifyCustomerModal();
          }}
        />
      ),
    });
  }

  function showCommercialInvoicePriceSelector() {
    if (
      !validateRequiredShipmentFields(
        [
          "consigneeName",
          "addr",
          "notifyPartyName",
          "notifyParty",
          "deliveryAddr",
        ].map((key) => [key, state[key]]),
      )
    ) {
      return;
    }
    navigate(`/shipment/doc/commercial-invoice/${state.id}`);
  }

  // default to US if no EU area
  const shippingZone = state.customer.shippingZone;

  return (
    <ShipmentContext.Provider
      value={{
        state,
        dispatch,
        prepareShippingMark,
        changeShipmentInvoiceRowProduct,
        bomSelectorModal,
        updateShipment,
        updateShipmentInvoiceRow,
      }}
    >
      <div className="flex flex-1 flex-col bg-gray-100 dark:bg-gray-900">
        <main className="flex flex-1 flex-col gap-8 p-4 lg:p-8">
          <CustomerInfo
            customer={state.customer}
            invoiceNumber={invoiceNumber}
          />

          <div className="flex flex-col gap-4 rounded-xl bg-white p-8 dark:bg-gray-800 dark:text-gray-300">
            <StatusForm
              shipmentId={state.id}
              initialStatus={state.latestStatus ? state.latestStatus : "0"}
              options={shipmentMeta.shipmentStatusOptions}
            />
            <ShipmentStatus errorOptions={shipmentMeta.errors} />
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-8">
            <div className="card flex flex-col gap-3 rounded-xl p-8 lg:flex-1">
              <div className="flex justify-between">
                <h5>Consignee</h5>
                <Button onClick={() => chooseCustomerAddress("addr")}>
                  Choose
                </Button>
              </div>
              <div className="flex items-center gap-3">
                <label className="w-16 shrink-0">Name</label>
                <Input
                  className="flex-1"
                  value={state.consigneeName}
                  onChange={(e) =>
                    onChangeShipment({ consigneeName: e.target.value })
                  }
                  onBlur={(e) =>
                    updateShipment({ consigneeName: e.target.value })
                  }
                  placeholder="Input name if it is different from the customer."
                />
              </div>
              <div className="flex items-baseline gap-3">
                <label className="w-16 shrink-0">Address</label>
                <Text
                  rows={3}
                  className="h-auto text-sm"
                  value={state.addr}
                  onChange={(e) => onChangeShipment({ addr: e.target.value })}
                  onBlur={(e) => updateShipment({ addr: e.target.value })}
                />
              </div>
            </div>

            <div className="card flex flex-col gap-3 rounded-xl p-8 lg:flex-1">
              <div className="flex justify-between">
                <h5>Delivery</h5>
                <Button onClick={() => chooseCustomerAddress("deliveryAddr")}>
                  Choose
                </Button>
              </div>
              <div className="flex items-center gap-3">
                <label className="w-16 shrink-0">Name</label>
                <Input
                  className="flex-1"
                  value={state.deliveryName}
                  onChange={(e) =>
                    onChangeShipment({ deliveryName: e.target.value })
                  }
                  onBlur={(e) =>
                    updateShipment({ deliveryName: e.target.value })
                  }
                  placeholder="Input name if it is different from the customer."
                />
              </div>
              <div className="flex items-baseline gap-3">
                <label className="w-16 shrink-0">Address</label>
                <Text
                  rows={3}
                  className="h-auto text-sm"
                  value={state.deliveryAddr}
                  onChange={(e) =>
                    onChangeShipment({ deliveryAddr: e.target.value })
                  }
                  onBlur={(e) =>
                    updateShipment({ deliveryAddr: e.target.value })
                  }
                />
              </div>
            </div>
            <div className="card flex flex-col gap-3 rounded-xl p-8">
                <div className="flex justify-between">
                  <h5>Notify Party</h5>
                  <Button onClick={() => chooseCustomerAddress("notifyParty")}>
                    Choose
                  </Button>
                </div>
                <div className="flex items-center gap-3">
                  <label className="w-16 shrink-0">Name</label>
                  <Input
                    className="flex-1"
                    value={state.notifyPartyName}
                    onChange={(e) =>
                      onChangeShipment({ notifyPartyName: e.target.value })
                    }
                    onBlur={(e) =>
                      updateShipment({ notifyPartyName: e.target.value })
                    }
                    placeholder="Input name if it is different from the customer."
                  />
                </div>

                <div className="flex items-baseline gap-3">
                  <label className="w-16 shrink-0">Address</label>
                  <Text
                    rows={3}
                    className="h-auto text-sm"
                    value={state.notifyParty}
                    onChange={(e) =>
                      onChangeShipment({ notifyParty: e.target.value })
                    }
                    onBlur={(e) =>
                      updateShipment({ notifyParty: e.target.value })
                    }
                  />
                </div>
            </div>

            <div className="card flex flex-col gap-3 rounded-xl p-8 lg:flex-1">
              <h5>Shipping Mark</h5>
              <label>Name</label>
              <Text
                rows={3}
                className="h-auto text-sm"
                value={state.shippingMark}
                onChange={(e) =>
                  onChangeShipment({ shippingMark: e.target.value })
                }
                onBlur={(e) => updateShipment({ shippingMark: e.target.value })}
              />
            </div>
          </div>

          <ShipmentInvoices>
            <div className="flex flex-col gap-4">
              <h3>Loading Plan</h3>
              <ContainerizationView
                totalCbm={Number(state.totalCbm)}
                shippingZone={shippingZone}
                defaultDisabledContainers={["40GP"]}
                summaryFirst
              />
            </div>
          </ShipmentInvoices>

          <section>
            <Logistics
              shippingTerms={shipmentMeta.shippingTerms}
              shippingZone={shippingZone}
            />
          </section>

          <Attachments />
        </main>

        <div
          id="footerControls"
          className="sticky bottom-0 left-0 right-0 flex shrink-0 py-4 border-t border-gray-200 dark:border-gray-700 overflow-auto scrolling-touch backdrop-blur-xl backdrop-filter bg-opacity-50 bg-gray-100 dark:bg-gray-900 dark:bg-opacity-70 border-b-gray-200 w-full"
        >
          <div className="px-8 flex gap-6 text-sm whitespace-nowrap">
            <Button onClick={showTemplates}>Notify Customer</Button>
            <div
              aria-hidden="true"
              className="w-px h-5 self-center bg-gray-300 dark:bg-gray-600"
            />
            <div>
              <PackingListButton
                shipmentId={state.id}
                onBeforeGenerate={canGeneratePackingList}
              />
            </div>
            <div>
              <Button onClick={showCustomFormGenerator}>Custom Form</Button>
            </div>
            <div>
              <Button
                disabled={updateShipmentRes.loading}
                onClick={showCommercialInvoicePriceSelector}
              >
                Commercial Invoice
              </Button>
            </div>
            <div
              aria-hidden="true"
              className="w-px h-5 self-center bg-gray-300 dark:bg-gray-600"
            />
            <div>
              <Button
                onClick={() =>
                  bomSelectorModal.present({
                    title: "Generate Shipment BOM",
                    children: (
                      <BomExportForm
                        hide={bomSelectorModal.hide}
                        shipment={state}
                      />
                    ),
                  })
                }
              >
                BOM
              </Button>
            </div>
            <div>
              <BookingFormButton
                shipmentId={state.id}
                invoiceNumber={state.invoiceNumber}
              />
            </div>
          </div>
        </div>
      </div>
    </ShipmentContext.Provider>
  );
}

export default ShipmentContainer;
